import { useState } from "react";
import { useParams, useNavigate } from "react-router";
import { toast } from "sonner";
import {
  IconPlus,
  IconTrash,
  IconSettings,
  IconPackage,
  IconChevronLeft,
  IconChevronDown,
  IconChevronRight,
  IconDownload,
} from "@tabler/icons-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { PageHeader } from "@/components/ui/page-header";
import { EmptyState } from "@/components/ui/empty-state";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  useReleases,
  useEventReleases,
  useAddReleaseToEvent,
  useRemoveReleaseFromEvent,
  useToggleEventReleaseDisabled,
} from "@/api/hooks/use-releases";
import { useEvent } from "@/api/hooks";
import { Switch } from "@/components/ui/switch";
import {
  asEventId,
  asReleaseId,
  type EventId,
  type ReleaseId,
} from "@/types/identifiers";
import { formatValue } from "@/domain/calibration-params";

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function AddReleaseDialog({
  open,
  onOpenChange,
  eventId,
  existingReleaseIds,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  eventId: EventId;
  existingReleaseIds: Set<string>;
}) {
  const { data: releases } = useReleases();
  const addMut = useAddReleaseToEvent(eventId);
  const [selectedId, setSelectedId] = useState<string>("");

  const availableReleases =
    releases?.filter((r) => !existingReleaseIds.has(r.id)) ?? [];

  const handleSubmit = () => {
    if (!selectedId) return;
    addMut.mutate(
      { releaseId: asReleaseId(selectedId) },
      {
        onSuccess: () => {
          toast.success("Release added to event");
          onOpenChange(false);
          setSelectedId("");
        },
        onError: (err) => toast.error(err.message),
      }
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add Release to Event</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <Select value={selectedId} onValueChange={setSelectedId}>
            <SelectTrigger>
              <SelectValue placeholder="Select a release" />
            </SelectTrigger>
            <SelectContent>
              {availableReleases.length === 0 ? (
                <div className="px-2 py-4 text-sm text-muted-foreground text-center">
                  No releases available
                </div>
              ) : (
                availableReleases.map((r) => (
                  <SelectItem key={r.id} value={r.id}>
                    {r.name} (v{r.build_version})
                  </SelectItem>
                ))
              )}
            </SelectContent>
          </Select>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={!selectedId || addMut.isPending}
            >
              {addMut.isPending ? "Adding..." : "Add Release"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function EventReleasesPage() {
  const { eventId: rawEventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  const eventId = asEventId(rawEventId ?? "");

  const { data: eventReleases, isLoading } = useEventReleases(eventId);
  const { data: event } = useEvent(eventId);
  const removeMut = useRemoveReleaseFromEvent(eventId);
  const toggleDisabledMut = useToggleEventReleaseDisabled(eventId);
  const [addOpen, setAddOpen] = useState(false);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  const existingReleaseIds = new Set(
    eventReleases?.map((er) => er.release_id) ?? []
  );

  const toggleExpanded = (id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleRemove = (releaseId: ReleaseId, name: string) => {
    if (confirm(`Remove "${name}" from this event?`)) {
      removeMut.mutate(releaseId, {
        onSuccess: () => toast.success("Release removed from event"),
        onError: (err) => toast.error(err.message),
      });
    }
  };

  if (isLoading) {
    return (
      <div>
        <Skeleton className="h-8 w-48 rounded-lg" />
        <div className="mt-6 space-y-2">
          {[...Array(3)].map((_, i) => (
            <Skeleton key={i} className="h-16 w-full rounded-lg" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="mb-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate(`/events/${eventId}`)}
          className="text-muted-foreground"
        >
          <IconChevronLeft className="h-4 w-4 mr-1" />
          Back to Builds
        </Button>
      </div>

      <PageHeader
        title="Event Releases"
        subtitle={event?.name}
        action={
          <Button size="sm" onClick={() => setAddOpen(true)}>
            <IconPlus className="mr-1.5 h-4 w-4" />
            Add Release
          </Button>
        }
      />

      <p className="text-sm text-muted-foreground mt-2 mb-6">
        Configure which firmware releases are used at this event and define
        common calibration targets.
      </p>

      {!eventReleases || eventReleases.length === 0 ? (
        <EmptyState
          icon={IconPackage}
          message="No releases configured for this event"
          className="py-16"
        />
      ) : (
        <div className="flex flex-col gap-2">
          {eventReleases.map((er) => {
            const isExpanded = expandedIds.has(er.id);
            const hasCommonCals = er.common_cals.length > 0;
            const sortedCals = hasCommonCals
              ? [...er.common_cals].sort((a, b) =>
                  (a.Name as string).localeCompare(b.Name as string)
                )
              : [];
            const borderColor = er.disabled ? "border-l-border" : "border-l-green-500";

            return (
              <div key={er.id}>
                <div
                  className={`flex items-center bg-card border-l-3 ${borderColor} pl-4 pr-5 py-3 transition-shadow hover:shadow-[0_1px_4px_rgba(0,0,0,0.04)] ${
                    isExpanded ? "" : "rounded-r-lg"
                  }`}
                >
                  <div className={`flex-1 min-w-0 ${er.disabled ? "opacity-60" : ""}`}>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-[13px]">{er.release_name}</span>
                      {er.disabled && (
                        <span className="text-[11px] text-muted-foreground">(disabled)</span>
                      )}
                      <span className="font-mono text-xs bg-muted px-2 py-0.5 rounded">
                        v{er.build_version}
                      </span>
                    </div>
                    <div className="mt-1 flex items-center text-[11px] text-muted-foreground">
                      <span>added {formatDate(er.created_at)}</span>
                      <span className="text-muted-foreground/40 mx-2">|</span>
                      {hasCommonCals ? (
                        <button
                          type="button"
                          onClick={() => toggleExpanded(er.id)}
                          className="inline-flex items-center gap-1 hover:text-foreground transition-colors"
                        >
                          {isExpanded ? (
                            <IconChevronDown className="h-3.5 w-3.5" />
                          ) : (
                            <IconChevronRight className="h-3.5 w-3.5" />
                          )}
                          <Badge variant="secondary">
                            {er.common_cals.length} parameter
                            {er.common_cals.length !== 1 ? "s" : ""}
                          </Badge>
                        </button>
                      ) : (
                        <span>No common cals configured</span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Switch
                      checked={!er.disabled}
                      onCheckedChange={(checked) =>
                        toggleDisabledMut.mutate({
                          releaseId: asReleaseId(er.release_id),
                          disabled: !checked,
                        })
                      }
                      disabled={toggleDisabledMut.isPending}
                    />
                    {hasCommonCals && (
                      <Button
                        variant="ghost"
                        size="sm"
                        asChild
                        className="text-muted-foreground hover:text-foreground"
                      >
                        <a
                          href={`/api/events/${eventId}/releases/${er.release_id}/common-cals.csv`}
                          download
                        >
                          <IconDownload className="h-4 w-4" />
                        </a>
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() =>
                        navigate(
                          `/events/${eventId}/releases/${er.release_id}/common-cals`
                        )
                      }
                      className="text-muted-foreground hover:text-foreground"
                    >
                      <IconSettings className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() =>
                        handleRemove(asReleaseId(er.release_id), er.release_name)
                      }
                      disabled={removeMut.isPending}
                      className="text-muted-foreground hover:text-destructive"
                    >
                      <IconTrash className="h-4 w-4" />
                    </Button>
                  </div>
                </div>

                {isExpanded && hasCommonCals && (
                  <div className={`bg-card border-l-3 ${borderColor} rounded-br-lg pl-6 pr-5 py-3`}>
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-muted-foreground text-xs">
                          <th className="text-left font-medium pb-1.5 pr-4">Parameter</th>
                          <th className="text-right font-medium pb-1.5 pr-4">Target Value</th>
                          <th className="text-left font-medium pb-1.5">Unit</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sortedCals.map((cal) => (
                          <tr key={cal.Name as string}>
                            <td className="py-0.5 pr-4 font-mono text-xs">
                              {cal.Name as string}
                            </td>
                            <td className="py-0.5 pr-4 text-right font-mono text-xs">
                              {formatValue(
                                cal.target_value as number | number[] | string | null,
                                cal.Accuracy as number,
                                cal.ChpDataType as string | undefined
                              )}
                            </td>
                            <td className="py-0.5 text-xs text-muted-foreground">
                              {(cal.Unit as string) || "-"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <AddReleaseDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        eventId={eventId}
        existingReleaseIds={existingReleaseIds}
      />
    </>
  );
}
